import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import {
  getGroupHealthSettings,
  updateGroupHealthSettings,
  UserKeys,
} from '../../../../api/users-api';
import { GroupHealthSettings } from '../../../../../types';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import { handleErrors } from '../../../../utils/errors';
import { Messages } from '../../../../utils/message-constants';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useUserSettings } from '../../../../utils/use-user-settings';
import {
  DirectoryRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Close from '@mui/icons-material/Close';
import HealthAndSafetyOutlined from '@mui/icons-material/HealthAndSafetyOutlined';
import LockOutlined from '@mui/icons-material/LockOutlined';
import SettingsOutlined from '@mui/icons-material/SettingsOutlined';

export interface GroupHealthSettingsModalProps {
  open: boolean;
  onClose: () => void;
}

const THRESHOLD_OPTIONS = [30, 60, 90, 180, 365];
const DEFAULT_THRESHOLD = 90;

const DEFAULT_SETTINGS: GroupHealthSettings = {
  flagOwnerless: true,
  flagManagerless: true,
  flagInactive: true,
  inactivityThresholdDays: DEFAULT_THRESHOLD,
};

export default function GroupHealthSettingsModal({
  open,
  onClose,
}: GroupHealthSettingsModalProps) {
  const queryClient = useQueryClient();
  const { createEvent } = useAnalytics();
  const { changeLogRoles } = useUserSettings();
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);

  const { data: settings } = useQuery({
    queryKey: [UserKeys.GroupHealthSettings],
    queryFn: getGroupHealthSettings,
  });

  const { control, handleSubmit, reset, getValues } =
    useForm<GroupHealthSettings>({
      defaultValues: DEFAULT_SETTINGS,
      values: {
        flagOwnerless:
          settings?.flagOwnerless ?? DEFAULT_SETTINGS.flagOwnerless,
        flagManagerless:
          settings?.flagManagerless ?? DEFAULT_SETTINGS.flagManagerless,
        flagInactive: settings?.flagInactive ?? DEFAULT_SETTINGS.flagInactive,
        inactivityThresholdDays:
          settings?.inactivityThresholdDays ?? DEFAULT_THRESHOLD,
      },
    });

  function handleClose() {
    reset();
    onClose();
  }

  const { mutate, isPending } = useMutation({
    mutationFn: (settings: GroupHealthSettings) =>
      updateGroupHealthSettings(settings, changeLogMessage),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [UserKeys.GroupHealthSettings],
      });
      queryClient.invalidateQueries({
        queryKey: [UserKeys.GroupHealthSummary],
      });
      queryClient.invalidateQueries({
        queryKey: [UserKeys.GroupsIndexedPage],
      });
      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Updating(
          'the group health settings'
        ),
      });
    },
    onSettled: () => {
      createEvent(analytics.directory.groups.update_group_health_settings, {
        module: ModuleList.modules.directory.name,
        section: ModuleList.modules.directory.sections.groups,
      });
      setOpenChangeLogDialog(false);
    },
  });

  const onSubmit: SubmitHandler<GroupHealthSettings> = (
    data: GroupHealthSettings
  ) => {
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        DirectoryRoles.EditGroupHealthSettings.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate(data);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <Box
          component="form"
          onSubmit={handleSubmit(onSubmit)}
          noValidate
          sx={{ display: 'flex', flexDirection: 'column' }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ px: 3, py: 2, borderBottom: 1, borderColor: 'divider' }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <SettingsOutlined fontSize="small" />
              <Typography variant="h6">Groups Settings</Typography>
            </Stack>
            <IconButton size="small" onClick={handleClose} aria-label="close">
              <Close fontSize="small" />
            </IconButton>
          </Stack>

          <Stack direction="row" sx={{ minHeight: 400 }}>
            <Stack
              sx={{
                width: 160,
                borderRight: 1,
                borderColor: 'divider',
                py: 2,
                px: 1,
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{
                  px: 1.5,
                  py: 1,
                  borderRadius: 1,
                  bgcolor: 'primary.50',
                  color: 'primary.main',
                }}
              >
                <HealthAndSafetyOutlined fontSize="small" />
                <Typography variant="body2" fontWeight={600}>
                  Health
                </Typography>
              </Stack>
            </Stack>

            <DialogContent sx={{ flex: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Applies org-wide. Groups with no posts, joins, or leaves within
                the threshold are flagged Inactive in the Groups grid.
              </Typography>

              <Stack spacing={1.5}>
                <Controller
                  name="flagOwnerless"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={field.value}
                          onChange={(event) =>
                            field.onChange(event.target.checked)
                          }
                        />
                      }
                      label={
                        <Stack>
                          <Typography variant="body2">
                            Flag ownerless groups
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            No owner assigned
                          </Typography>
                        </Stack>
                      }
                    />
                  )}
                />
                <Controller
                  name="flagManagerless"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={field.value}
                          onChange={(event) =>
                            field.onChange(event.target.checked)
                          }
                        />
                      }
                      label={
                        <Stack>
                          <Typography variant="body2">
                            Flag managerless groups
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            No manager to answer membership requests
                          </Typography>
                        </Stack>
                      }
                    />
                  )}
                />
                <Controller
                  name="flagInactive"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={field.value}
                          onChange={(event) =>
                            field.onChange(event.target.checked)
                          }
                        />
                      }
                      label={
                        <Stack>
                          <Typography variant="body2">
                            Flag inactive groups
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            No activity within the threshold
                          </Typography>
                        </Stack>
                      }
                    />
                  )}
                />
              </Stack>

              <Typography
                variant="body2"
                fontWeight={600}
                sx={{ mt: 3, mb: 1 }}
              >
                Inactivity Threshold
              </Typography>
              <Controller
                name="inactivityThresholdDays"
                control={control}
                render={({ field }) => (
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {THRESHOLD_OPTIONS.map((days) => {
                      const selected = field.value === days;
                      const label =
                        days === DEFAULT_THRESHOLD
                          ? `${days} days (default)`
                          : `${days} days`;
                      return (
                        <Chip
                          key={days}
                          label={label}
                          clickable
                          variant="outlined"
                          onClick={() => field.onChange(days)}
                          sx={
                            selected
                              ? {
                                  bgcolor: 'primary.50',
                                  borderColor: 'primary.main',
                                  color: 'primary.main',
                                }
                              : undefined
                          }
                        />
                      );
                    })}
                  </Stack>
                )}
              />

              <Paper
                variant="outlined"
                sx={{
                  mt: 3,
                  p: 1.5,
                  bgcolor: 'action.hover',
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1,
                }}
              >
                <LockOutlined fontSize="small" color="disabled" />
                <Typography variant="caption" color="text.secondary">
                  Requires the Directory Settings permission. Changes take
                  effect on the next health scan and are recorded in the audit
                  log with your identity.
                </Typography>
              </Paper>
            </DialogContent>
          </Stack>

          <DialogActions
            sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}
          >
            <Button onClick={handleClose}>Cancel</Button>
            <Button variant="contained" type="submit" disabled={isPending}>
              Save
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
      {openChangeLogDialog && (
        <ChangeLogDialog
          open={openChangeLogDialog}
          handleClose={() => setOpenChangeLogDialog(false)}
          changeLogMessage={changeLogMessage}
          setChangeLogMessage={setChangeLogMessage}
          mutate={() => {
            mutate(getValues());
          }}
          mutateLoading={isPending}
        />
      )}
    </>
  );
}
